import { Box } from "@gemini/core"
import { EarthIcon, EnvelopeIcon, LocationDotIcon, PhoneIcon } from "@gemini/icons"
import { useEffect, useState } from "react"

import { useAgencyData } from "../../api/useAgencyData"
import { formatAddress } from "../../utils/formatting"
import { LogError } from "../../utils/logging"
import { TRACKING_VALUES } from "../../utils/trackingEvents"
import { Logo } from "../Logo"
import { SafeLink } from "../SafeLink/SafeLink"
import { TrackPageView } from "../Tracking/TrackPageView"

const TextBlock = ({ title, children }: { title?: string; children?: React.ReactNode }) => {
  return (
    children && (
      <Box>
        {title && <Box typography="typography.body.14.bold">{title}</Box>}
        <Box typography="typography.body.16.regular" whiteSpace="pre-line">
          {children}
        </Box>
      </Box>
    )
  )
}

export const ImprintContent = () => {
  const {
    data: { profile, medias },
  } = useAgencyData()
  const legalInformation = profile.legalInformation?.countrySpecific.de
  // fallback to companyName
  const legalCompanyName =
    profile.agencyInfo?.agencyLegalInformation?.legalCompanyName || profile.agencyInfo?.companyName
  const logo = medias?.logo
  const term = medias?.terms
  const { INTERMEDIARY_IMPRINT } = TRACKING_VALUES.PAGE

  const [pdfUrl, setPdfUrl] = useState<string>("")
  useEffect(() => {
    const fetchPdf = async () => {
      try {
        if (!term?.url) return
        const response = await fetch(term?.url)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPdfUrl(url)
      } catch (error) {
        LogError("Error fetching the PDF", error as Error)
      }
    }

    fetchPdf()
  }, [])

  return (
    <Box display="flex" flexDirection="column" gap="spacing.24">
      <TrackPageView pageTitle={INTERMEDIARY_IMPRINT} />
      <Logo
        testId="immonet-imprint-logo"
        logo={logo}
        size="80"
        alignSelf="center"
        companyName={profile.agencyInfo?.companyName}
      />
      <Box
        testId="immonet-imprint-box-1"
        display="flex"
        flexDirection="column"
        gap="spacing.8"
        alignItems="flex-start"
      >
        {legalCompanyName && <Box typography="typography.body.16.bold">{legalCompanyName}</Box>}
        {legalInformation?.address && (
          <Box display="flex" gap="spacing.4">
            <LocationDotIcon />
            <Box>{formatAddress(legalInformation.address)}</Box>
          </Box>
        )}
        {legalInformation?.telephone && (
          <SafeLink href={`tel:${legalInformation.telephone}`} icon={PhoneIcon}>
            {legalInformation.telephone}
          </SafeLink>
        )}
        {legalInformation?.email && (
          <SafeLink href={`mailto:${legalInformation.email}`} icon={EnvelopeIcon}>
            {legalInformation.email}
          </SafeLink>
        )}
        {legalInformation?.website?.url && (
          <Box display="flex" gap="spacing.4">
            <EarthIcon />
            <SafeLink href={legalInformation.website.url} isExternal={true}>
              {legalInformation.website.url}
            </SafeLink>
          </Box>
        )}
      </Box>
      <Box testId="immonet-imprint-box-2" display="flex" flexDirection="column" gap="spacing.8">
        <Box typography="typography.body.16.bold">Rechtliche Angaben</Box>
        <TextBlock title="Gesetzlicher Vertreter">
          {legalInformation?.legalRepresentative}
        </TextBlock>
        <TextBlock title="Registernummer">{legalInformation?.legalRegistrationNumber}</TextBlock>
        <TextBlock title="USt-IdNr.">{legalInformation?.vatNumber}</TextBlock>
        <TextBlock title="Registergericht">{legalInformation?.registrationCourt}</TextBlock>
        <TextBlock title="Gewerbeerlaubnis gem. §34c GewO wurde erteilt durch">
          {legalInformation?.registrationIssuer}
        </TextBlock>
        <TextBlock title="Zuständige Aufsichtsbehörde">
          {legalInformation?.controllingAuthority}
        </TextBlock>
        <TextBlock title="Bemerkungen">{legalInformation?.remarks}</TextBlock>
      </Box>
      <Box testId="immonet-imprint-box-3" display="flex" flexDirection="column" gap="spacing.8">
        <Box typography="typography.body.16.bold">Verbraucherinformationen</Box>
        {legalInformation?.onlineDispute?.arbitrationBoardEnabled ? (
          <>
            <Box>
              Die Firma nimmt an einem Streitbeilegungsverfahren vor einer
              Verbraucherschlichtungsstelle teil:
            </Box>
            <TextBlock title="Name">
              {legalInformation?.onlineDispute?.arbitrationBoardName}
            </TextBlock>
            <TextBlock title="Adresse">
              {legalInformation?.onlineDispute?.arbitrationBoardStreet}
              <br />
              {legalInformation.onlineDispute.arbitrationBoardCityAndZip}
            </TextBlock>
            <TextBlock title="Website">
              {legalInformation?.onlineDispute?.arbitrationBoardUrl && (
                <SafeLink href={legalInformation.onlineDispute.arbitrationBoardUrl} isExternal>
                  {legalInformation.onlineDispute.arbitrationBoardUrl}
                </SafeLink>
              )}
            </TextBlock>
          </>
        ) : (
          <Box>
            Die Firma ist nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen.
          </Box>
        )}
        {pdfUrl && (
          <Box>
            <SafeLink href={pdfUrl} isExternal={true}>
              AGB
            </SafeLink>
          </Box>
        )}
      </Box>
    </Box>
  )
}
